import { useEffect, useRef } from "react";
import type { SupportCaseThreadItem, SupportCaseThreadSide } from "@gaole/shared";
import { fmtDate, rm } from "../../lib/format";
import { openCaseFilePreview } from "../../lib/caseAttachments";
import { toast } from "../../stores/toast";
import { ApiError } from "../../lib/api";

interface CaseChatThreadProps {
  caseId: string;
  items: SupportCaseThreadItem[];
  viewerSide: SupportCaseThreadSide;
  className?: string;
}

function fileLabel(key: string, index: number): string {
  const base = key.split("/").pop() ?? key;
  return base.length > 24 ? `File ${index + 1}` : base;
}

export function CaseChatThread({ caseId, items, viewerSide, className }: CaseChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items]);

  async function openFile(params: { fieldKey: string } | { commentId: string; fileKey: string }) {
    try {
      await openCaseFilePreview(caseId, params);
    } catch (e) {
      toast("error", e instanceof ApiError ? e.message : "Could not open attachment");
    }
  }

  return (
    <div className={`space-y-2.5 overflow-y-auto ${className ?? ""}`}>
      {items.map((item) => {
        const mine = item.side === viewerSide;
        return (
          <div
            key={item.id}
            className={`flex max-w-[88%] flex-col gap-1 ${mine ? "ml-auto items-end" : "items-start"}`}
          >
            <div
              className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                mine ? "bg-gradient-brand text-white" : "border border-line bg-hover text-foreground"
              }`}
            >
              {item.kind === "opening" && item.openingLines?.length ? (
                <dl className="space-y-1">
                  {item.openingLines.map((line) => (
                    <div key={line.key}>
                      <dt className="text-[10px] uppercase tracking-wide text-white/50">{line.label}</dt>
                      <dd>
                        {line.type === "file" && line.fileKey ? (
                          <button
                            type="button"
                            className="font-semibold text-brand-pink hover:underline"
                            onClick={() => void openFile({ fieldKey: line.key })}
                          >
                            View attachment
                          </button>
                        ) : line.type === "amount" ? (
                          rm(Number(line.displayValue) || 0)
                        ) : (
                          line.displayValue
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                item.body
              )}
              {item.fileKeys.length > 0 && (
                <ul className={`mt-2 space-y-1 ${item.kind === "opening" ? "" : "border-t border-line/60 pt-2"}`}>
                  {item.fileKeys.map((key, i) => (
                    <li key={key}>
                      <button
                        type="button"
                        className="text-left text-[11px] font-semibold text-brand-pink hover:underline"
                        onClick={() => void openFile({ commentId: item.id, fileKey: key })}
                      >
                        {fileLabel(key, i)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <span className="px-1 text-[10px] text-muted">{fmtDate(item.createdAt)}</span>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
